import type { PresentationPage } from 'paintscout'
import cloneDeep from 'lodash/cloneDeep'
import { createLogger } from '@paintscout/logger'

const log = createLogger('presentation-drag-blocks')

export const PRESENTATION_BLOCK_DND_TYPE = 'presentation-block'

export interface BlockDragLocation {
  pageKey: string
  index: number
}

export interface BlockDragResult {
  source: BlockDragLocation
  destination?: BlockDragLocation
}

/**
 * Moves a section (block) from `source` to `destination`, where each location is identified
 * by its page key rather than page index, since sections live in `pages[].sections[]` rather
 * than a single flat array. Supports both reordering within a page and moving across pages.
 */
export function reorderPresentationBlocks(
  pages: PresentationPage[],
  source: BlockDragLocation,
  destination: BlockDragLocation
): PresentationPage[] {
  const newPages = cloneDeep(pages)
  const sourcePage = newPages.find((page) => page.key === source.pageKey)
  const destinationPage = newPages.find((page) => page.key === destination.pageKey)

  if (!sourcePage || !destinationPage) {
    // a rendered Droppable/Draggable id not matching a page key is an invariant violation
    // (stale dnd result, or pages mutated between drag start and drop) rather than a normal
    // user-input case, so it's worth surfacing rather than silently no-op-ing
    log.error('reorderPresentationBlocks: source or destination page key not found', {
      sentry: true,
      sourcePageKey: source.pageKey,
      destinationPageKey: destination.pageKey,
      foundSourcePage: !!sourcePage,
      foundDestinationPage: !!destinationPage
    })
    return pages
  }

  const [movedSection] = sourcePage.sections.splice(source.index, 1)
  destinationPage.sections.splice(destination.index, 0, movedSection)

  return newPages
}

/**
 * Translates a raw dnd drop result into a new `pages` array, or `null` when there's nothing
 * to do (dropped outside any droppable, or dropped back in its original spot).
 */
export function getReorderedPagesFromDragResult(
  pages: PresentationPage[],
  result: BlockDragResult
): PresentationPage[] | null {
  const { source, destination } = result

  if (!destination) {
    return null
  }
  if (source.pageKey === destination.pageKey && source.index === destination.index) {
    return null
  }

  return reorderPresentationBlocks(pages, source, destination)
}
